import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { auth } from '../firebase';
import PostScheduler from './social/PostScheduler';
import ContentQueue from './social/ContentQueue';
import SocialConnections from './social/SocialConnections';
import SocialAnalytics from './social/SocialAnalytics';
import { CalendarIcon, QueueIcon, ConnectionLinkIcon, AnalyticsIcon, PremiumIcon, QuickIcon } from './icons';
import '../styles/homeQuietLounge.css';

type TabType = 'planner' | 'queue' | 'connections' | 'analytics';

interface SocialMediaHubProps {
  onClose?: () => void;
}

const SocialMediaHub: React.FC<SocialMediaHubProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('planner');
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser: User | null) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FAFAF9]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A9488] mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'planner' as TabType, name: 'Post Planner', Icon: CalendarIcon, description: 'Plan nieuwe posts met kalender' },
    { id: 'queue' as TabType, name: 'Content Queue', Icon: QueueIcon, description: 'Beheer geplande en gepubliceerde posts' },
    { id: 'connections' as TabType, name: 'Connecties', Icon: ConnectionLinkIcon, description: 'Koppel social media accounts' },
    { id: 'analytics' as TabType, name: 'Analytics', Icon: AnalyticsIcon, description: 'Bekijk performance metrics' }
  ];

  return (
    <div className="quiet-lounge min-h-screen p-6 bg-[#FAFAF9]">
      <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 shadow-sm rounded-t-lg mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Social Media Management Center
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Beheer en analyseer je social media content
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <div className="inline-flex items-center px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-full text-xs">
                  <PremiumIcon size={14} weight="regular" className="mr-1 text-[#7A9488]" />
                  <span className="text-gray-600">Gratis gebruikers: Posts krijgen</span>
                  <QuickIcon size={12} weight="regular" className="mx-0.5 text-[#7A9488]" />
                  <span className="text-gray-600">SuperKonnected branding</span>
                </div>
                <div className="inline-flex items-center px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-full text-xs">
                  <CalendarIcon size={14} weight="regular" className="mr-1 text-[#7A9488]" />
                  <span className="text-gray-600">Plan posts vanuit Dashboard → Klik "Plannen"</span>
                </div>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-stone-200">
        <div className="px-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  transition-all duration-200
                  ${activeTab === tab.id
                    ? 'border-[#7A9488] text-[#4F6660]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-stone-300'
                  }
                `}
              >
                <tab.Icon size={20} weight="regular" className="mr-2 text-current" />
                <div className="text-left">
                  <div className="font-semibold">{tab.name}</div>
                  <div className="text-xs text-gray-400">{tab.description}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-b-lg shadow-sm border border-stone-200 border-t-0 min-h-[600px]">
        {activeTab === 'planner' && <PostScheduler userId={user.uid} />}
        {activeTab === 'queue' && <ContentQueue userId={user.uid} onNavigateToConnections={() => setActiveTab('connections')} />}
        {activeTab === 'connections' && <SocialConnections userId={user.uid} />}
        {activeTab === 'analytics' && <SocialAnalytics userId={user.uid} />}
      </div>
      </div>
    </div>
  );
};

export default SocialMediaHub;

